import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, useMemo } from 'react';

const CustomFilter = forwardRef((props, ref) => {
  const { api, colDef, filterParams } = props;
  const [values, setValues] = useState([]);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('or');
  const appliedSelectedRef = useRef({});
  const filteredValues = useMemo(() => {
    return values.filter((i) => (search ? i.toLowerCase().includes(search.toLowerCase()) : true));
  }, [values, search]);
  const [init, setInit] = useState(false);

  useEffect(() => {
    setInit(true);
  }, []);

  useEffect(() => {
    const field = colDef.field;
    const set = new Set();

    api.forEachNode((node) => {
      let v;
      if (props?.colDef?.valueGetter) {
        v = props.colDef.valueGetter(node);
      } else {
        v = node.data?.[field];
      }

      if (Array.isArray(v)) {
        v = v.filter(Boolean);
        if (v.length) {
          v.forEach((item) => {
            set.add(String(item));
          });
        } else {
          set.add('(Blanks)');
        }
      } else {
        set.add(String(v));
      }
    });

    const sorted = Array.from(set)
      .sort()
      .sort((a, b) => (a === '(Blanks)' ? -1 : b === '(Blanks)' ? 1 : 0));
    setValues(sorted);

    if (Object.keys(appliedSelectedRef.current).length === 0) {
      const selectdAll = sorted.reduce((acc, v) => {
        acc[v] = true;
        return acc;
      }, {});
      setSelected(selectdAll);
    } else {
      setSelected({ ...appliedSelectedRef.current });
    }
  }, [api, colDef.field]);

  const allSelected = useMemo(() => {
    const filtered = new Set(filteredValues);
    const selectedValues = new Set(Object.keys(selected));
    return setsAreEqual(filtered, selectedValues);
  }, [filteredValues, selected]);

  useEffect(() => {
    if (init) {
      if (filterType === 'or') {
        if (!values.includes('(Blanks)')) {
          const selectBlanks = allSelected;
          setValues(['(Blanks)', ...values]);
          if (selectBlanks) {
            setSelected((prev) => ({ ...prev, '(Blanks)': true }));
          }
        }
      } else {
        if (values.includes('(Blanks)')) {
          setValues(values.filter((s) => s !== '(Blanks)'));
          delete selected['(Blanks)'];
        }
      }
    }
  }, [filterType]);

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      if (appliedSelectedRef.current === null) return true;
      return Object.keys(appliedSelectedRef.current).length > 0;
    },

    doesFilterPass(params) {
      const active = appliedSelectedRef.current;
      if (!active) return false;
      const keys = Object.keys(active);
      if (!keys.length) return true;

      let raw;
      if (props?.colDef?.valueGetter) {
        raw = props.colDef.valueGetter(params.node);
      } else {
        raw = params.node.data?.[colDef.field];
      }

      if (!Array.isArray(raw)) {
        raw = raw == null ? [] : [raw];
      }

      if (filterType === 'and') {
        return keys.every((key) => raw.includes(key));
      } else {
        if (raw.length === 0 && active['(Blanks)']) {
          return true;
        }
        return raw.some((item) => {
          return keys.includes(item);
        });
      }
    },

    getModel() {
      if (appliedSelectedRef.current === null) return null;
      const keys = Object.keys(appliedSelectedRef.current);
      return keys.length ? { values: keys } : null;
    },

    setModel(model) {
      const next = {};
      if (model?.values) {
        model.values.forEach((v) => {
          next[v] = true;
        });
      }
      appliedSelectedRef.current = next;
      setSelected(next);
    },
  }));

  const onApply = () => {
    if (Object.keys(selected).length === Object.keys(values).length) {
      appliedSelectedRef.current = {};
    } else if (Object.keys(selected).length === 0) {
      appliedSelectedRef.current = null;
    } else {
      appliedSelectedRef.current = { ...selected };
    }
    props.filterChangedCallback();

    if (filterParams?.closeOnApply) {
      api.hidePopupMenu();
    }
  };

  const onReset = () => {
    const selectdAll = values.reduce((acc, v) => {
      acc[v] = true;
      return acc;
    }, {});
    setSelected(selectdAll);
    appliedSelectedRef.current = {};
    props.filterChangedCallback();
  };

  const toggle = (value) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[value]) delete next[value];
      else next[value] = true;
      return next;
    });
  };

  function setsAreEqual(a, b) {
    if (a.size > 0 && a.size !== b.size) return false;
    for (const val of a) {
      if (!b.has(val)) return false;
    }
    return true;
  }

  return (
    <div
      style={{
        width: '210px',
        overflow: 'hidden',
        padding: '12px',
        borderRadius: 4,
        color: '#24282a',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div>
        <div
          style={{
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <input
              name="method"
              type="radio"
              value="or"
              checked={filterType === 'or'}
              onChange={() => {
                setFilterType('or');
              }}
            />
            <span>OR</span>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <input
              name="method"
              type="radio"
              value="and"
              checked={filterType === 'and'}
              style={{
                margin: 0,
              }}
              onChange={() => {
                setFilterType('and');
              }}
            />
            <span>AND</span>
          </label>
        </div>
        <div>
          <input
            style={{
              display: 'block',
              height: '30px',
              padding: '4px 8px',
              width: '100%',
              margin: 0,
              minHeight: 'calc(var(--ag-grid-size) * 4)',
              borderRadius: 'var(--ag-border-radius)',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              color: 'inherit',
              fontFamily: 'inherit',
              border: 'var(--ag-borders-input) var(--ag-input-border-color)',
              paddingLeft: '26px',
              'background-color': 'var(--ag-background-color)',
            }}
            type="text"
            placeholder="Search..."
            onInput={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div
        style={{
          flexGrow: 1,
          flexShrink: 1,
          maxHeight: '144px',
          overflowY: 'auto',
          overflowX: 'hidden',
          margin: '12px 0',
          borderRadius: 4,
          width: '100%',
          textOverflow: 'ellipsis',
          marginRight: '-16px',
        }}
      >
        {(() => {
          if (!filteredValues.length) {
            return <div>No matches.</div>;
          }
          return (
            <>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  height: '24px',
                  marginBottom: 4,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 400,
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => {
                    if (allSelected) {
                      filteredValues.forEach((v) => {
                        if (selected[v]) toggle(v);
                      });
                    } else {
                      filteredValues.forEach((v) => {
                        if (!selected[v]) toggle(v);
                      });
                    }
                  }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}
                >
                  (Select All)
                </span>
              </label>
              {filteredValues.map((v) => (
                <label
                  key={v}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    height: '24px',
                    marginBottom: 4,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  <input type="checkbox" checked={!!selected[v]} onChange={() => toggle(v)} />
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                    }}
                  >
                    {v}
                  </span>
                </label>
              ))}
            </>
          );
        })()}
      </div>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
        <button
          style={{
            'font-family': 'inherit',
            appearance: 'none',
            'border-radius': 'var(--ag-border-radius)',
            border: 'solid 1px var(--ag-input-border-color)',
            'background-color': 'var(--ag-background-color)',
            padding: 'var(--ag-grid-size) calc(var(--ag-grid-size) * 2)',
            cursor: 'pointer',
          }}
          onClick={onApply}
        >
          Apply
        </button>
        <button
          style={{
            'font-family': 'inherit',
            appearance: 'none',
            'border-radius': 'var(--ag-border-radius)',
            border: 'solid 1px var(--ag-input-border-color)',
            'background-color': 'var(--ag-background-color)',
            padding: 'var(--ag-grid-size) calc(var(--ag-grid-size) * 2)',
            cursor: 'pointer',
          }}
          onClick={onReset}
        >
          Reset
        </button>
      </div>
    </div>
  );
});

export default CustomFilter;
